import PubNub from 'pubnub'
import Config from '@/config.json'

export class ChatService {
  pubnub
  existingListeners
  historyMessagesCounter

  constructor({ uuid = '', channels = [], historyMessagesCounter = 25 }) {
    this.pubnub = new PubNub({
      subscribeKey: Config.VUE_APP_PUBNUB_SUBSCRIBE_KEY,
      publishKey: Config.VUE_APP_PUBNUB_PUBLISH_KEY,
      logVerbosity: false, // set true for development
      restore: true,
      autoNetworkDetection: true,
      uuid, //, ssl: true
    })

    this.pubnub.subscribe({
      channels: channels,
      withPresence: true,
      autoload: 10,
    })

    this.historyMessagesCounter = historyMessagesCounter
  }

  activateListeners({
    onMessage,
    onPresence,
    onSignal,
    onObjects,
    onMessageAction,
    onFile,
    onStatus,
  }) {
    this.existingListeners = {
      ...(onMessage ? { message: onMessage } : {}),
      ...(onPresence ? { presence: onPresence } : {}),
      ...(onSignal ? { signal: onSignal } : {}),
      ...(onObjects ? { objects: onObjects } : {}),
      ...(onMessageAction ? { messageAction: onMessageAction } : {}),
      ...(onFile ? { file: onFile } : {}),
      ...(onStatus ? { status: onStatus } : {}),
    }

    this.pubnub.addListener(this.existingListeners)
  }

  async subscribeNewChannels(channels) {
    if (Array.isArray(channels) && channels.length > 0) {
      await this.pubnub.subscribe({
        channels: [...channels],
        withPresence: true,
        autoload: 10,
      })
    }
  }

  deactivateListeners() {
    this.pubnub.removeListener(this.existingListeners)
  }

  // messages
  getChatMessages({ channels, counter }, cb) {
    this.pubnub.fetchMessages(
      {
        channels,
        count: counter,
        includeMeta: true,
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }

  getAllUnreadMessages(
    { channels, lastReadTimetokens },
    cb,
    previousResponseChannels = {},
  ) {
    const CHANNELS_LIMIT = 50
    const channelsList = channels.slice(0, CHANNELS_LIMIT)
    const remainingChannels = channels.slice(CHANNELS_LIMIT)
    const readTimetokensList = lastReadTimetokens.slice(0, CHANNELS_LIMIT)
    const remainingReadTimetokens = lastReadTimetokens.slice(CHANNELS_LIMIT)

    this.pubnub.messageCounts(
      {
        channels: channelsList,
        channelTimetokens: readTimetokensList,
      },
      (status, response) => {
        const updatedChannels = {
          ...previousResponseChannels,
          ...response.channels,
        }

        if (remainingChannels.length > 0) {
          this.getAllUnreadMessages(
            {
              channels: remainingChannels,
              lastReadTimetokens: remainingReadTimetokens,
            },
            cb,
            updatedChannels,
          )
        } else {
          cb(status, { ...response, channels: updatedChannels })
        }
      },
    )
  }
  async newAddChatMessage(
    channel,
    {
      id,
      messageText,
      messageLanguage,
      needAck = false,
      uuid,
      file,
      mediaLibraryFiles = null,
      giphyFile = null,
      replyMessageTimetoken = null,
    },
  ) {
    const message = {
      id,
      text: messageText,
      is_read: false,
      updated: false,
      updated_at: null,
      deleted: false,
      deleted_at: null,
      user: uuid,
      reactions: [],
      needAck,
      ack: false,
    }

    if (messageLanguage) {
      message.lng = messageLanguage
    }

    if (mediaLibraryFiles) {
      message['mediaLibraryFiles'] = mediaLibraryFiles
    }

    if (giphyFile) {
      message['giphyFile'] = giphyFile
    }

    if (replyMessageTimetoken) {
      message['replyMessageTimetoken'] = replyMessageTimetoken
    }

    const messageSize = await this.calculate_payload_size(channel, message)

    // Limit of pubnub message 32768 bytes: https://support.pubnub.com/hc/en-us/articles/360051495932-Calculating-message-payload-size-before-publishing
    if (messageSize >= 32768) {
      return { error: true, errorType: 'MESSAGE_TO_BIG' }
    } else {
      if (file) {
        return await this.pubnub.sendFile({
          channel: channel,
          message,
          file: {
            data: file.data,
            name: file.name,
            mimeType: file.mimeType || 'application/json',
          },
        })
      } else {
        return await this.pubnub.publish({
          channel,
          message,
        })
      }
    }
  }
  async addChatMessage(
    channel,
    {
      id,
      messageText,
      messageLanguage,
      needAck = false,
      uuid,
      file,
      mediaLibraryFiles = null,
      giphyFile = null,
      replyMessageTimetoken = null,
    },
    cb = () => {},
  ) {
    const message = {
      id,
      text: messageText,
      is_read: false,
      updated: false,
      updated_at: null,
      deleted: false,
      deleted_at: null,
      user: uuid,
      reactions: [],
      needAck,
      ack: false,
    }

    if (messageLanguage) {
      message.lng = messageLanguage
    }

    if (mediaLibraryFiles) {
      message['mediaLibraryFiles'] = mediaLibraryFiles
    }

    if (giphyFile) {
      message['giphyFile'] = giphyFile
    }

    if (replyMessageTimetoken) {
      message['replyMessageTimetoken'] = replyMessageTimetoken
    }

    const messageSize = await this.calculate_payload_size(channel, message)

    // Limit of pubnub message 32768 bytes: https://support.pubnub.com/hc/en-us/articles/360051495932-Calculating-message-payload-size-before-publishing
    if (messageSize >= 32768) {
      cb({ error: true, errorType: 'MESSAGE_TO_BIG' })
    } else {
      if (file) {
        this.pubnub.sendFile(
          {
            channel: channel,
            message,
            file: {
              data: file.data,
              name: file.name,
              mimeType: file.mimeType || 'application/json',
            },
          },
          (status, response) => {
            cb(status, response)
          },
        )
      } else {
        this.pubnub.publish(
          {
            channel,
            message,
          },
          (status, response) => {
            cb(status, response)
          },
        )
      }
    }
  }

  calculate_payload_size(channel, message) {
    return encodeURIComponent(channel + JSON.stringify(message)).length + 100
  }

  sendPushNotification(channel, { notificationText }, cb = () => {}) {
    this.pubnub.publish(
      {
        message: {
          pn_gcm: {
            notification: {
              body: notificationText,
              vibrate: 1,
              sound: 'default',
            },
          },
          text: notificationText,
          pn_apns: {
            aps: {
              alert: notificationText,
              sound: 'default',
            },
            pn_push: [
              {
                push_type: 'alert',
                auth_method: 'token',
                targets: [
                  {
                    environment: Config.VUE_APP_PUBNUB_PUSH_ENV,
                    topic: Config.VUE_APP_PUBNUB_PUSH_TOPIC,
                  },
                ],
                version: 'v2',
              },
            ],
          },
        },
        channel: channel,
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }

  updatePubNubMessage(channel, { messageBody, data }, cb = () => {}) {
    this.pubnub.publish(
      {
        channel,
        message: {
          ...messageBody,
          ...data,
          updated: true,
          updatedAt: Date.now(),
        },
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }

  // signal
  sendChatSignal(channel, type, cb = () => {}) {
    this.pubnub.signal(
      {
        message: type,
        channel,
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }

  // actions
  addMessageAction(channel, { timetoken, type, value }, cb = () => {}) {
    this.pubnub.addMessageAction(
      {
        channel,
        messageTimetoken: timetoken,
        action: {
          type,
          value,
        },
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }

  removeMessageAction(
    channel,
    { messageTimetoken, actionTimetoken },
    cb = () => {},
  ) {
    this.pubnub.removeMessageAction(
      {
        channel,
        messageTimetoken,
        actionTimetoken,
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }

  // files
  async getFileUrl({ channel, name, id }) {
    return await this.pubnub.getFileUrl({ channel, name, id })
  }

  downloadFile({ file, channel }) {
    const { id, name } = file

    return this.pubnub.downloadFile({
      channel,
      id,
      name,
    })
  }

  // history
  getMoreMessages({ channel, date, count }, cb = () => {}) {
    return this.pubnub.fetchMessages(
      {
        channels: [channel],
        count: count || this.historyMessagesCounter,
        includeMeta: true,
        includeMessageActions: true,
        start: date,
      },
      (status, response) => {
        cb(status, response)
      },
    )
  }
}
